import React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { BaseApplicationState } from '@common/react/store';
import { BaseUser } from '@common/typescript/objects/BaseUser';

import { BaseContentPage } from '@app/objects/ContentPage';

interface HelmetProviderProps<T> {
	path?: string;
	showLapOfLoveTitle?: boolean;
	ignoreServerPage?: boolean;
	children: React.ReactNode | ((page: T) => React.ReactNode);
}

const HelmetProvider: <T extends BaseContentPage>(props: HelmetProviderProps<T>) => React.ReactElement = ({
	path,
	ignoreServerPage = false,
	showLapOfLoveTitle = true,
	children,
}) => {
	const hostOptions = useSelector((state: BaseApplicationState<BaseUser>) => state.hostOptions.item);

	return (
		<ServerPageProvider
			path={path}
			getIgnoreServerPage={() => ignoreServerPage}
			inner={(page) => {
				const title = `${page?.content?.title
					? `${page.content.title}${showLapOfLoveTitle && !page.content.title.endsWith('Lap of Love') ? ' | Lap of Love' : ''}`
					: 'Lap of Love'
				}`;
				const description = page?.content?.description || '';
				const canonical = page?.canonical
					? `${hostOptions.hostServer}${page.canonical}`
					: undefined;
				const image = page?.content?.image
					? page.content.image.startsWith('http')
						? page.content.image
						: `${hostOptions.hostServer}${page.content.image}`.replace(/([^:]\/)\/+/g, '$1')
					: `${hostOptions.hostServer}img/logo-630h-optimized.png`;

				return (
					<>
						{!ignoreServerPage && page && (
							<Helmet>
								<title>{title}</title>
								<meta name="description" content={description} />

								<meta property="og:title" content={title} />
								<meta property="og:description" content={description} />
								{canonical && <meta property="og:url" content={canonical} />}
								<meta property="og:image" content={image} />

								<meta name="twitter:title" content={title} />
								<meta name="twitter:description" content={description} />
								<meta name="twitter:image" content={image} />

								{canonical && <link rel="canonical" href={canonical} />}
							</Helmet>
						)}
						{typeof children === 'function' ? children(page) : children}
					</>
				);
			}}
		/>
	);
};

export default HelmetProvider;
